import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, ShoppingBag, Star, Truck, Award, ShieldCheck } from "lucide-react";
import { fetchProduct, fetchRelated, fetchProducts, inr } from "../lib/api";
import { useSEO } from "../lib/seo";
import { metaTrack, productData } from "../analytics/metaPixel";
import { useShop } from "../context/ShopContext";
import { ProductCard } from "../components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWishlisted, setCartOpen, addRecentlyViewed, recent } = useShop();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const imgWrap = useRef(null);

  useSEO(product ? {
    title: `${product.name} — ${product.fabric} Ilkal Saree | R V Ilkal Sarees`,
    description: product.description.slice(0, 155),
    canonicalPath: `/product/${product.id}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        image: product.images,
        description: product.description,
        brand: { "@type": "Brand", name: "R V Ilkal Sarees" },
        category: "Ilkal Sarees",
        offers: {
          "@type": "Offer",
          priceCurrency: "INR",
          price: product.price,
          availability: product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          url: `${window.location.origin}/product/${product.id}`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
          { "@type": "ListItem", position: 2, name: "Ilkal Sarees", item: `${window.location.origin}/shop` },
          { "@type": "ListItem", position: 3, name: product.name },
        ],
      },
    ],
  } : { title: "Ilkal Saree | R V Ilkal Sarees" });

  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    fetchProduct(id).then((p) => {
      setProduct(p);
      addRecentlyViewed(p);
      metaTrack("ViewContent", productData(p));
    }).catch(() => {});
    fetchRelated(id).then(setRelated).catch(() => {});
  }, [id]);

  useEffect(() => {
    Promise.all(recent.slice(0, 4).map((r) => fetchProduct(r.id).catch(() => null))).then((res) =>
      setRecentProducts(res.filter(Boolean))
    );
  }, [recent]);

  if (!product) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-16 animate-pulse" data-testid="product-loading">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="aspect-[3/4] bg-surface" />
          <div className="space-y-4 pt-4">
            <div className="h-8 bg-surface w-3/4" />
            <div className="h-6 bg-surface w-1/3" />
            <div className="h-24 bg-surface" />
          </div>
        </div>
      </main>
    );
  }

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  const onMove = (e) => {
    const rect = imgWrap.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin(`${x}% ${y}%`);
  };

  const buyNow = () => {
    addToCart(product, 1, true);
    navigate("/checkout");
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-28 lg:pb-14" data-testid="product-page">
      <nav className="text-xs text-charcoal/50 tracking-wide mb-8" data-testid="breadcrumb">
        <Link to="/" className="hover:text-maroon">Home</Link>
        <span className="mx-2">/</span>
        <Link to={`/shop?category=${product.category}`} className="hover:text-maroon capitalize">{product.category}</Link>
        <span className="mx-2">/</span>
        <span className="text-charcoal">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div
            ref={imgWrap}
            className="relative aspect-[3/4] overflow-hidden bg-surface cursor-zoom-in"
            onMouseEnter={() => setZoom(true)}
            onMouseLeave={() => setZoom(false)}
            onMouseMove={onMove}
            data-testid="product-main-image"
          >
            <img
              src={product.images[activeImg]}
              alt={`${product.name} — ${product.image_labels?.[activeImg] || "view"}`}
              className="w-full h-full object-cover transition-transform duration-200"
              style={zoom ? { transform: "scale(1.8)", transformOrigin: origin } : {}}
            />
            {product.is_new && (
              <span className="absolute top-4 left-4 bg-gold text-charcoal text-[10px] tracking-[0.15em] uppercase px-3 py-1.5">New</span>
            )}
            {discount > 0 && (
              <span className="absolute top-4 right-4 bg-maroon text-ivory text-[10px] tracking-[0.15em] uppercase px-3 py-1.5" data-testid="product-discount-badge">-{discount}%</span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3 mt-3">
            {product.images.map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                data-testid={`thumbnail-${i}`}
                className={`aspect-square overflow-hidden border transition-colors ${activeImg === i ? "border-maroon" : "border-gold/25 hover:border-gold"}`}
              >
                <img src={src} alt={product.image_labels?.[i] || `View ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <p className="text-xs text-charcoal/40 mt-2 text-center">{product.image_labels?.[activeImg]} — hover the main image to zoom</p>
        </div>

        <div>
          <p className="uppercase tracking-[0.25em] text-xs font-semibold text-gold">{product.fabric} • {product.category}</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-charcoal mt-3" data-testid="product-title">{product.name}</h1>

          <div className="flex items-center gap-2 mt-3">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-gold text-gold" : "text-gold/30"}`} />
              ))}
            </div>
            <span className="text-sm text-charcoal/50" data-testid="product-rating">{product.rating} · {product.reviews_count} reviews</span>
          </div>

          <div className="flex items-baseline gap-3 mt-5">
            <span className="text-3xl text-maroon font-semibold" data-testid="product-price">{inr(product.price)}</span>
            {product.original_price && (
              <span className="text-lg text-charcoal/40 line-through" data-testid="product-original-price">{inr(product.original_price)}</span>
            )}
            {discount > 0 && <span className="text-sm text-maroon font-semibold">{discount}% off</span>}
          </div>
          <p className="text-xs text-charcoal/50 mt-1">Inclusive of all taxes</p>

          <p className="text-base leading-relaxed text-charcoal/70 mt-6">{product.description}</p>

          <div className="flex flex-wrap gap-2 mt-5">
            {product.colors.map((c) => (
              <span key={c} className="text-xs border border-gold/40 text-charcoal/70 px-3 py-1.5">{c}</span>
            ))}
            {product.occasions.map((o) => (
              <span key={o} className="text-xs bg-surface text-charcoal/70 px-3 py-1.5">{o}</span>
            ))}
          </div>

          <div className="hidden lg:grid grid-cols-2 gap-3 mt-8">
            <button
              onClick={() => { addToCart(product); setCartOpen(true); }}
              disabled={!product.in_stock}
              data-testid="add-to-cart-btn"
              className="flex items-center justify-center gap-2 border border-maroon text-maroon text-xs tracking-[0.18em] uppercase py-4 hover:bg-maroon hover:text-ivory transition-colors disabled:border-charcoal/40 disabled:text-charcoal/40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            >
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> {product.in_stock ? "Add to Cart" : "Out of Stock"}
            </button>
            <button
              onClick={buyNow}
              disabled={!product.in_stock}
              data-testid="buy-now-btn"
              className="bg-maroon text-ivory text-xs tracking-[0.18em] uppercase py-4 hover:bg-maroon-dark transition-colors disabled:bg-charcoal/60 disabled:cursor-not-allowed"
            >
              Buy Now
            </button>
          </div>
          {product.stock_qty != null && product.in_stock && product.stock_qty <= 5 && (
            <p className="text-xs text-maroon mt-3 tracking-wide" data-testid="low-stock-note">Only {product.stock_qty} left in stock — order soon</p>
          )}

          <button
            onClick={() => toggleWishlist(product)}
            data-testid="product-wishlist-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 border border-gold/40 text-charcoal/70 text-xs tracking-[0.18em] uppercase py-4 hover:border-gold hover:text-maroon transition-colors"
          >
            <Heart className={`w-4 h-4 ${isWishlisted(product.id) ? "fill-maroon text-maroon" : ""}`} strokeWidth={1.5} />
            {isWishlisted(product.id) ? "Saved to Wishlist" : "Add to Wishlist"}
          </button>

          <div className="grid grid-cols-3 gap-3 mt-8 text-center">
            {[
              { icon: Truck, label: "Pan India Delivery" },
              { icon: Award, label: "Premium Quality" },
              { icon: ShieldCheck, label: "Authenticity Assured" },
            ].map((b) => (
              <div key={b.label} className="border border-gold/20 py-4 px-2">
                <b.icon className="w-5 h-5 text-maroon mx-auto" strokeWidth={1.25} />
                <p className="text-[10px] tracking-[0.12em] uppercase text-charcoal/60 mt-2">{b.label}</p>
              </div>
            ))}
          </div>

          <Accordion type="multiple" defaultValue={["details"]} className="mt-8" data-testid="product-details-accordion">
            <AccordionItem value="details" className="border-gold/20">
              <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Saree Details</AccordionTrigger>
              <AccordionContent>
                <dl className="space-y-3 text-sm">
                  <div className="flex gap-4"><dt className="w-24 text-charcoal/50">Fabric</dt><dd className="text-charcoal" data-testid="detail-fabric">{product.details.fabric}</dd></div>
                  <div className="flex gap-4"><dt className="w-24 text-charcoal/50">Length</dt><dd className="text-charcoal" data-testid="detail-length">{product.details.length}</dd></div>
                  <div className="flex gap-4"><dt className="w-24 text-charcoal/50">Blouse</dt><dd className="text-charcoal" data-testid="detail-blouse">{product.details.blouse}</dd></div>
                </dl>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-gold/20">
              <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Care Instructions</AccordionTrigger>
              <AccordionContent><p className="text-sm text-charcoal/70" data-testid="detail-care">{product.details.care}</p></AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping" className="border-gold/20">
              <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Shipping & Returns</AccordionTrigger>
              <AccordionContent><p className="text-sm text-charcoal/70" data-testid="detail-shipping">{product.details.shipping} For returns, write to rvilkalsarees@gmail.com or call customer care on +91 94499 09269.</p></AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20 sm:mt-28" data-testid="related-products">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-charcoal">You May Also Like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8 mt-8">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {recentProducts.length > 0 && (
        <section className="mt-20" data-testid="recently-viewed">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-charcoal">Recently Viewed</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8 mt-8">
            {recentProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-ivory/95 backdrop-blur border-t border-gold/30 grid grid-cols-2" data-testid="mobile-sticky-buy">
        <button
          onClick={() => { addToCart(product); setCartOpen(true); }}
          disabled={!product.in_stock}
          data-testid="mobile-add-to-cart-btn"
          className="py-4 text-xs tracking-[0.18em] uppercase text-maroon font-semibold disabled:text-charcoal/40"
        >
          {product.in_stock ? "Add to Cart" : "Out of Stock"}
        </button>
        <button
          onClick={buyNow}
          disabled={!product.in_stock}
          data-testid="mobile-buy-now-btn"
          className="py-4 text-xs tracking-[0.18em] uppercase bg-maroon text-ivory disabled:bg-charcoal/60"
        >
          Buy Now
        </button>
      </div>
    </main>
  );
}
